
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  *
  * The source file is located at:
  *   /codebuild/output/src1738611268/src/vl-app/pages/forgot-password.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from '@hooks/useRouter';
import { useDispatch } from 'react-redux';
import { Row, Col, Alert, Button, Form, Collapse } from 'antd';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import Input from '@components/UI/Input/Input';
import Password from '@components/FormInputs/Password';
import VerificationCode from '@components/FormInputs/VerificationCode';
import ConfirmPassword from '@components/FormInputs/ConfirmPassword';
import BackButton from '@components/UI/BackButton/BackButton';
import { useMutation } from '@tanstack/react-query';
import useRedirectToAxaSSO from '@hooks/useRedirectToAxaSSO';
import { useNotification } from '../contexts/NotificationContext/NotificationContext';
import AuthHeader from '@components/Layouts/Header/AuthHeader';

const StyledForgotPasswordPage = styled.div`
  min-height: 100dvh;
  display: flex;
  align-items: center;
  flex-direction: column;
  .login-layout {
    display: flex;
    max-width: 600px;
    margin: auto;

    .login-title {
      text-align: center;
    }

    .login-desc {
      color: var(--productGray);
      font-size: 14px;
      margin: 12px 0 20px 0;
      font-weight: normal;
    }

    .login-button {
      width: 100%;
      height: 46px;
    }

    .login-input {
      height: 3rem !important;
      border-radius: 2px;

      &:focus-within {
        box-shadow: 0 0 0 2px var(--ant-primary-color-outline);
      }

      :global(input) {
        flex-grow: 2;

        &:focus-visible {
          outline: none;
        }
      }
    }
  }
`;

const StyledCollapse = styled(Collapse)`
  margin-bottom: 0.5rem;

  .ant-collapse-header {
    padding: 12px 0 !important;
  }

  .ant-collapse-expand-icon {
    width: 20px;
    padding-inline-end: unset !important;
    margin-right: 1rem;
    justify-content: center;
    background: #e6f4ff80;
    color: #00008f;
    border: 1px solid #91caff;
    border-radius: 3px;
  }

  .ant-collapse-header-text {
    color: #00008f;
    font-weight: bold;
  }

  .ant-collapse-content-box {
    background: #e6f4ff80;
    border: 1px solid #91caff;
    border-radius: 3px;
    color: #00008f;
  }
`;

const StyledList = styled.ul`
  margin: 0 0 1rem;
  padding-left: 1.5rem;

  &:only-child {
    margin-bottom: 0;
  }

  li {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    &::marker {
      color: #3b82f6;
    }
  }
`;

const StyledInfoAlert = styled(Alert)`
  margin-bottom: 1rem;
  align-items: start;

  .info-block-message {
    color: #00008f;

    a:hover {
      text-decoration: underline;
    }
  }

  .ant-alert-icon {
    margin: 5px 16px 0 0;
  }
`;

export const UnstyledButton = styled(Button)`
  padding: 0;
  height: auto;
  border: none;
  background: none;
  box-shadow: none;
  font: inherit;
  color: inherit;

  &:hover,
  &:focus,
  &:active {
    background: none !important;
    text-decoration: underline;
    color: inherit;
  }
`;

const ResetPasswordInfoSection = () => {
  const { t } = useTranslation('common');
  const redirectToAxaSSO = useRedirectToAxaSSO();
  const isClinician = typeof window !== 'undefined' && window.location.hostname.includes('clin');
  const isNHS = typeof window !== 'undefined' && window.location.hostname.includes('nhs');

  if (isClinician || isNHS) return null;

  return (
    <StyledInfoAlert
      role="presentation"
      message={
        <span className="info-block-message">
          <strong>{t('Note:')}</strong>{' '}
          {t(
            "Do not use this password reset if you originally logged in with your AXA Health credentials via the blue 'AXA LOG IN' button. Instead, you can update or reset your password via the"
          )}{' '}
          <UnstyledButton data-testid="sso-login-link" onClick={redirectToAxaSSO}>
            <strong>{t('AXA Health login page.')}</strong>
          </UnstyledButton>
        </span>
      }
      type="info"
      showIcon
    />
  );
};

const VerificationCodeFAQSection = () => {
  const { t } = useTranslation('common');
  return (
    <StyledCollapse
      ghost
      items={[
        {
          label: <span>{t("What if I haven't received a code?")}</span>,
          children: (
            <>
              <StyledList>
                <li>{t('Check your email/SMS inbox')}</li>
                <li>{t('Check your junk folder')}</li>
                <li>
                  {t('Look for an email from')} <strong>{t('support@hbsuk.co.uk')}</strong>
                </li>
              </StyledList>
              <ResetPasswordInfoSection />
            </>
          )
        }
      ]}
    />
  );
};

const VerificationCodeSentMessage = () => {
  const { t } = useTranslation('common');
  return (
    <StyledInfoAlert
      message={
        <span className="info-block-message">
          {t('We have sent a verification code to your preferred contact method (email or sms).')}
        </span>
      }
      type="info"
    />
  );
};

const PasswordExpiredInfoMessage = () => {
  const { t } = useTranslation('common');
  return (
    <StyledInfoAlert
      message={
        <div className="info-block-message">
          <StyledList>
            <li>{t('Your password has expired and needs to be updated for your security.')}</li>
            <li>{t('Use the verification code sent to you (via email or SMS) to set a new password.')}</li>
            <li>{t('Your new password must be different from your previous one.')}</li>
          </StyledList>
        </div>
      }
      type="info"
    />
  );
};

const ResetPasswordSteps = {
  EmailVerification: 'EmailVerification',
  ResetPasswordConfirm: 'ResetPasswordConfirm', //NOSONAR
  ResetExpiredPasswordConfirm: 'ResetExpiredPasswordConfirm' //NOSONAR
} as const;

type ResetPasswordStep = (typeof ResetPasswordSteps)[keyof typeof ResetPasswordSteps];

type VerifyEmailValues = {
  email: string;
};

type ResetPasswordConfirmFormValues = {
  verificationCode: string;
  password: string;
  confirmPassword: string;
};

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [form] = Form.useForm();
  const [resetPasswordStep, setResetPasswordStep] = useState<ResetPasswordStep>(ResetPasswordSteps.EmailVerification);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [codeValid, setCodeValid] = useState(false);
  const { t } = useTranslation('common');
  const { errorNotification } = useNotification();
  const onConfirmPasswordChanged = useCallback(
    (value: string) => {
      form.setFields([
        {
          name: 'confirmPassword',
          value,
          errors: []
        }
      ]);
    },
    [form]
  );
  const onPasswordChanged = useCallback(
    (value: string) => {
      form.setFieldsValue({
        password: value
      });
      setNewPassword(value);
    },
    [form]
  );
  const onCodeChanged = useCallback(
    (value: string) => {
      form.setFieldsValue({
        verificationCode: value
      });
    },
    [form]
  );

  useEffect(() => {
    if (passwordValid && form.getFieldError('password').length > 0) {
      form.validateFields(['password']);
    }
  }, [passwordValid, form]);

  useEffect(() => {
    if (codeValid && form.getFieldError('verificationCode').length > 0) {
      form.validateFields(['verificationCode']);
    }
  }, [codeValid, form]);

  useInitialisedStore();
  useEffect(() => {
    const expiredPasswordUserEmail = sessionStorage.getItem('passwordReset');
    if (expiredPasswordUserEmail) {
      setUserEmail(expiredPasswordUserEmail);
      setResetPasswordStep(ResetPasswordSteps.ResetExpiredPasswordConfirm);
      sessionStorage.removeItem('passwordReset');
    }
  }, []);

  const redirectToLogin = () => router.push('/login');

  const { mutate: resetPasswordMutation, isPending: isPendingResetPassword } = useMutation({
    mutationFn: async (email: string) => {
      const res = await dispatch(actions.userResetPassword(email));

      const requestResult = res?.CodeDeliveryDetails?.DeliveryMedium;
      const verificationCodeSent = requestResult === 'EMAIL';
      const userHasTemporaryPassword = requestResult === 'TEMP';

      if (!verificationCodeSent && !userHasTemporaryPassword) {
        throw new Error('Password reset failed');
      }

      return { userHasTemporaryPassword, email };
    },

    onSuccess: ({ userHasTemporaryPassword, email }) => {
      if (userHasTemporaryPassword) {
        sessionStorage.setItem('newUserForgotPassRedirect', email);
        redirectToLogin();
        return;
      }

      setUserEmail(email);
      setResetPasswordStep(ResetPasswordSteps.ResetPasswordConfirm);
    },

    onError: (error: Error) => {
      errorNotification({ message: 'Error', description: error.message });
    }
  });

  const { mutate: resetPasswordConfirmMutation, isPending: isPendingResetPasswordConfirm } = useMutation({
    mutationFn: async ({ code, password }: { code: string; password: string }) => {
      if (!userEmail) throw new Error('Missing user email');

      const res = await dispatch(
        actions.userResetPasswordConfirm({
          email: userEmail,
          code,
          password
        })
      );

      if (res.type === actionTypes.AUTH_LOGIN_RESETPASSWORD_CONFIRM_SUCCESS && !res.payload?.errorMessage) {
        return true;
      }

      const { payload: response } = res;

      const { errorMessage } =
        response instanceof Response
          ? await response.json()
          : { errorMessage: response?.errorMessage || 'Password reset failed' };

      throw new Error(errorMessage);
    },

    onSuccess: () => {
      sessionStorage.setItem('passwordResetSuccess', 'true');
      redirectToLogin();
    },

    onError: (error: Error) => {
      errorNotification({ message: 'Error', description: error.message });
    }
  });

  const onResetPassword = (formData: VerifyEmailValues) => resetPasswordMutation(formData.email);

  const onResetPasswordConfirm = (formData: ResetPasswordConfirmFormValues) =>
    resetPasswordConfirmMutation({ code: formData.verificationCode, password: formData.password });

  const isEmailVerificationStep = resetPasswordStep === ResetPasswordSteps.EmailVerification;
  const isResetPasswordConfirmStep = resetPasswordStep === ResetPasswordSteps.ResetPasswordConfirm;
  const isResetExpiredPasswordConfirmStep = resetPasswordStep === ResetPasswordSteps.ResetExpiredPasswordConfirm;

  return (
    <StyledForgotPasswordPage>
      <AuthHeader HeaderLeft={<BackButton text="Back" arrow onClick={redirectToLogin} data-testid="back-button" />} />
      <section className="section-layout login-layout">
        <Row justify="center" align="middle">
          <Col className="login-grid">
            {isEmailVerificationStep && (
              <>
                <h1 className="login-title">{t('Forgot Password')}</h1>
                <p className="login-desc text-center">
                  {t(
                    'Enter the email address of your HBSUK account and we’ll email you a verification code to reset your password.'
                  )}
                </p>

                <Form
                  name="basic"
                  layout="vertical"
                  onFinish={onResetPassword}
                  scrollToFirstError={{ focus: true, block: 'center' }}
                >
                  <Form.Item
                    label={t('Your Email Address')}
                    name="email"
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        type: 'email',
                        message: 'Please enter your e-mail address'
                      },
                      {
                        required: true,
                        message: 'Please enter your e-mail address'
                      }
                    ]}
                  >
                    <Input
                      autoComplete="off"
                      inputType="email"
                      data-testid="forgot-email-input"
                      placeholder={t('name@example.com')}
                      className="login-input"
                    />
                  </Form.Item>

                  <Form.Item className="button-container">
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="login-button"
                      data-testid="send"
                      loading={isPendingResetPassword}
                    >
                      {t('Send Verification Code')}
                    </Button>
                  </Form.Item>

                  <ResetPasswordInfoSection />
                </Form>
              </>
            )}

            {!isEmailVerificationStep && (
              <div style={{ padding: '0px 0px 150px 0px' }}>
                <h1 className="login-title">
                  {isResetExpiredPasswordConfirmStep ? t('Password Expired') : t('Reset Password')}
                </h1>
                <p className="login-desc">
                  {t('For account:')} <strong>{userEmail}</strong>
                </p>

                {isResetPasswordConfirmStep && <VerificationCodeSentMessage />}

                {isResetExpiredPasswordConfirmStep && <PasswordExpiredInfoMessage />}

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onResetPasswordConfirm}
                  scrollToFirstError={{ focus: true, block: 'center' }}
                >
                  <Form.Item
                    label={t('Enter Verification Code')}
                    name="verificationCode"
                    validateTrigger="onBlur"
                    className="login-form-item"
                    rules={[
                      {
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(new Error('Please input your code!'));
                          }
                          if (!codeValid) {
                            return Promise.reject(new Error('Please enter a valid code!'));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <VerificationCode onChange={onCodeChanged} setValid={setCodeValid} />
                  </Form.Item>

                  <VerificationCodeFAQSection />

                  <Form.Item
                    label={t('New Password')}
                    name="password"
                    validateTrigger="onBlur"
                    className="login-form-item"
                    rules={[
                      {
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(new Error('Please input your password!'));
                          }
                          if (!passwordValid) {
                            return Promise.reject(new Error('Please enter a valid password!'));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Password onChange={onPasswordChanged} setValid={setPasswordValid} />
                  </Form.Item>
                  <Form.Item
                    label={t('Confirm New Password')}
                    name="confirmPassword"
                    dependencies={['password']}
                    rules={[
                      {
                        required: true,
                        message: 'Please confirm your password!'
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('The two passwords that you entered do not match!'));
                        }
                      })
                    ]}
                    className="login-form-item"
                  >
                    <ConfirmPassword onChange={onConfirmPasswordChanged} password={newPassword} />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="login-button"
                      data-testid="send"
                      loading={isPendingResetPasswordConfirm}
                    >
                      {t('Reset Password')}
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            )}
          </Col>
        </Row>
      </section>
    </StyledForgotPasswordPage>
  );
};


export const Route = createFileRoute('/forgot-password')({ component: ForgotPassword });
